/**
 * Générateur — Il détermine de manière algébrique l'antécédent par une
 * fonction, dans des cas se ramenant à la résolution d'une équation du
 * premier degré. (STRICT : aucun second degré — v2, refonte campagne 3e.)
 * Attendu ID : 225 | Niveau : 3ème | Domaine : Fonctions (d13, O13)
 *
 * Source doc prof (B3S1.Ex.9-13). v2 2026-07-26 : la carte « nombre »
 * (fonction carré décalée, x² = a — hors attendu, cf. rapport famille C)
 * est remplacée par un QCM 1er degré en deux variantes : « équation »
 * (traduire la recherche d'antécédent en équation à résoudre) et « valeur »
 * (résoudre — distracteurs = erreurs types : image/antécédent confondus,
 * arrêt à ax = w − b, erreur de signe sur b).
 */

import { randomChoice, randInt, tex, makeQcm, makeTexteTrou } from '../../../utils';
import { frNb, rat, ratFrag, fmtAffine, decFrag, NOMS_FN } from './fonctions';

const ATTENDU_ID = 225;
const L_LINEAIRE = 'Images et antécédents par x ↦ ax';
const L_AFFINE = 'Antécédent par une fonction affine (équation)';
const L_NOMBRE = 'Antécédent : choisir l\'équation, choisir la valeur';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const coefNonNul = (lo, hi) => { let c = 0; while (c === 0) c = randInt(lo, hi); return c; };

// Bloc de solution : entier → un trou ; fraction → numérateur et dénominateur
// en deux trous d'une fraction dessinée (triple accolade, §0.5bis).
const solTpl = (r) => (r.q === 1 ? `{{${r.p}}}` : `\\dfrac{{{${r.p}}}}{{{${r.q}}}}`);

// Contrainte d'écriture de la réponse, adaptée à sa forme réelle (§0.1).
const consigneAntecedent = (r) => (r.q === 1
    ? ' L\'antécédent est un entier relatif : écris-le avec son signe.'
    : ' L\'antécédent est une fraction : écris le numérateur avec son signe, puis le dénominateur.');

// ═════════ « lineaire » — x ↦ ax ═════════

const genLineaireTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const a = coefNonNul(-9, 9);
    const v = coefNonNul(-8, 8);
    const w = a * coefNonNul(-9, 9);                       // cible d'antécédent → antécédent ENTIER
    const ant = rat(w, a);
    const phrase = `$${nom}(${v}) = {{${a * v}}} \\qquad \\text{antécédent de } ${w}`
        + ` \\text{ : } ${solTpl(ant)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${nom} : x \\mapsto ${fmtAffine(a, 0)}`)}.</p>`
        // « (résous ax = w) » donnait l'équation, c'est-à-dire la mise en
        // équation demandée.
        + `<p><b>Calcule l'image de ${v}, puis l'antécédent de ${w}.</b>${consigneAntecedent(ant)}</p>`,
        { ...opts(L_LINEAIRE), mode: 'saisie' });
    payload._meta.params = { nom, a, v, w };
    return payload;
};

// ═════════ « affine » — équation du 1er degré ═════════

const genAffineTrous = () => {
    const nom = randomChoice(NOMS_FN);
    let a; let b; let v; let w; let ant;
    for (let essai = 0; essai < 60; essai++) {
        a = coefNonNul(-9, 9); b = coefNonNul(-9, 9);
        v = coefNonNul(-8, 8);
        w = coefNonNul(-15, 15);
        ant = rat(w - b, a);
        if (ant.p !== 0 && Math.abs(ant.p) <= 30 && ant.q <= 12) break;
        ant = null;
    }
    if (!ant) { a = -3; b = 7; v = 8; w = 2; ant = rat(5, 3); } // repli (doc Ex.11)
    const phrase = `$${nom}(${v}) = {{${a * v + b}}} \\qquad \\text{antécédent de } ${w}`
        + ` \\text{ : } ${solTpl(ant)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${nom} : x \\mapsto ${fmtAffine(a, b)}`)}.</p>`
        // L'équation à résoudre n'est plus donnée. La contrainte d'écriture, elle,
        // reste obligatoire (§0.1) et s'adapte à la forme réelle de la réponse :
        // annoncer « fraction irréductible » quand l'antécédent tombe juste
        // faisait chercher une fraction inexistante pour un trou unique.
        + `<p><b>Calcule l'image de ${v}, puis l'antécédent de ${w}.</b>${consigneAntecedent(ant)}</p>`,
        { ...opts(L_AFFINE), mode: 'saisie' });
    payload._meta.params = { nom, a, b, v, w };
    return payload;
};

// ═════════ « nombre » — antécédent en QCM, 1er degré STRICT (v2) ═════════

/** Fragment LaTeX d'un nombre, parenthésé s'il est négatif : « (-14) ». */
const par = (x) => (x < 0 ? `\\left(${decFrag(x)}\\right)` : decFrag(x));

const genNombreQcm = () => {
    const nom = randomChoice(NOMS_FN);
    const variante = randomChoice(['equation', 'valeur']);
    // Par construction : antécédent ENTIER n choisi d'abord, puis w = a·n + b.
    let a; let b; let n; let w; let image; let arret; let signe; let ok = false;
    for (let essai = 0; essai < 80 && !ok; essai++) {
        a = coefNonNul(-9, 9);
        b = coefNonNul(-9, 9);
        n = coefNonNul(-9, 9);
        w = a * n + b;
        image = a * w + b;                                 // confusion image/antécédent : calcule nom(w)
        arret = w - b;                                     // s'arrête à ax = w − b (oubli de la division)
        signe = rat(w + b, a);                             // résout ax − b = w (erreur de signe sur b)
        ok = Math.abs(a) >= 2                              // sinon « oubli de la division » invisible
            && w !== 0 && w !== n && Math.abs(w) <= 60     // « = w » ≠ « = 0 », lisibilité
            && image !== n && arret !== n
            && !(signe.q === 1 && [n, image, arret].includes(signe.p))
            && image !== arret;
    }
    if (!ok) { a = 2; b = -6; n = -4; w = -14; image = -34; arret = -8; signe = rat(-20, 2); }
    const enonce = `<p>On pose ${tex(`${nom} : x \\mapsto ${fmtAffine(a, b)}`)}.</p>`;
    let payload;
    if (variante === 'equation') {
        const chBonne = tex(`${fmtAffine(a, b)} = ${decFrag(w)}`);
        const chImage = tex(`x = ${decFrag(a)} \\times ${par(w)} ${b < 0 ? `- ${frNb(-b)}` : `+ ${frNb(b)}`}`);
        const chZero = tex(`${fmtAffine(a, b)} = 0`);
        const chSigne = tex(`${fmtAffine(a, -b)} = ${decFrag(w)}`);
        payload = makeQcm([chBonne, chImage, chZero, chSigne], chBonne,
            `${enonce}<p><b>Pour déterminer l'antécédent de ${tex(decFrag(w))} par ${nom}, `
            + 'quelle équation faut-il résoudre ?</b></p>',
            opts(L_NOMBRE));
    } else {
        const chBonne = tex(decFrag(n));
        const chImage = tex(decFrag(image));
        const chArret = tex(decFrag(arret));
        const chSigne = tex(ratFrag(signe));
        payload = makeQcm([chBonne, chImage, chArret, chSigne], chBonne,
            // « Détermine-le de manière algébrique, en résolvant une équation »
            // indiquait la méthode, alors que le choix de la méthode fait partie
            // de la tâche (les distracteurs sanctionnent justement le tâtonnement).
            `${enonce}<p><b>Quel est l'antécédent de ${tex(decFrag(w))} par ${nom} ?</b></p>`,
            opts(L_NOMBRE));
    }
    payload._meta.params = { nom, a, b, n, w, variante };
    return payload;
};

const GENERATORS = {
    lineaire_texte_trou_saisie: genLineaireTrous,
    affine_texte_trou_saisie: genAffineTrous,
    nombre_qcm_unique: genNombreQcm,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'lineaire', label: L_LINEAIRE, formatsList: buildFormats('lineaire') },
    { id: 'affine', label: L_AFFINE, formatsList: buildFormats('affine') },
    { id: 'nombre', label: L_NOMBRE, formatsList: buildFormats('nombre') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
